/**
 * Store global de autenticación - Zen
 * Maneja: tokens JWT, usuario actual, tenant activo y lista de tenants.
 *
 * Relacionado con: AUT-01, AUT-02
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { jwtDecode } from 'jwt-decode'

// ─── Types ────────────────────────────────────────────────────────────────────
export interface TenantInfo {
  schema: string
  name: string
  roles: string[]
  permissions: Record<string, string[]>
  disabled_modules?: string[]
  logo_url?: string | null
  domain?: string | null
  country?: string | null   // ISO 3166-1 alpha-2: 'CL', 'PE', 'AR', etc.
}

export interface AuthUser {
  id: string
  email: string
  full_name: string
  is_platform_admin: boolean
  roles?: string[]
}

/** Datos básicos del registro de empleado vinculado al User del ERP (Caso B del portal). */
export interface EmployeeRecord {
  id: string
  rut: string
  position: string
  active: boolean
}

/**
 * Señal "Estudio Contable" por-usuario: presente solo si el usuario pertenece a
 * un estudio activo (vía FirmMembership en el schema público). `null` si no
 * pertenece → el frontend NO muestra el portal de cartera. Es ORTOGONAL al RBAC
 * por-tenant: el rol dentro del estudio (`firm_role`) decide qué ve en el portal.
 */
export interface FirmSession {
  id: string
  name: string
  firm_role: 'firm_admin' | 'firm_staff'
}

// JWT payload es slim: solo contiene identidad, sin tenants/permisos.
// Los permisos se reciben en el response body del login y se cachean en Redis en el backend.
interface JwtPayload {
  user_id: string
  email: string
  full_name: string
  is_platform_admin: boolean
  exp: number
}

interface AuthState {
  // Estado
  accessToken: string | null
  user: AuthUser | null
  tenants: TenantInfo[]                // Lista de tenants del usuario
  activeTenantSchema: string | null    // Tenant activo de la sesión (AUT-02)
  activeTenant: TenantInfo | null
  /** Caso B del portal: registro de empleado vinculado al User del ERP (null si no aplica). */
  employeeRecord: EmployeeRecord | null
  /** Estudio Contable: estudio al que pertenece el usuario (null si no pertenece a ninguno). */
  firm: FirmSession | null
  // Computed
  isAuthenticated: boolean
  isTokenExpired: () => boolean

  // Acciones
  setTokens: (access: string) => void
  setTenants: (tenants: TenantInfo[]) => void
  setActiveTenant: (schema: string) => void
  setEmployeeRecord: (record: EmployeeRecord | null) => void
  setFirm: (firm: FirmSession | null) => void
  hydrateFromSession: (session: { user: AuthUser; tenants: TenantInfo[] }) => void
  logout: () => void
}

// ─── Store ────────────────────────────────────────────────────────────────────
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      tenants: [],
      activeTenantSchema: null,
      activeTenant: null,
      employeeRecord: null,
      firm: null,
      isAuthenticated: false,

      isTokenExpired: () => {
        const { accessToken } = get()
        if (!accessToken) {
          // Sin access token: el refresh token vive en httpOnly cookie,
          // dejamos que el interceptor intente renovarlo
          return false
        }
        try {
          const decoded = jwtDecode<JwtPayload>(accessToken)
          return decoded.exp * 1000 < Date.now()
        } catch {
          return true
        }
      },

      setTokens: (access: string) => {
        try {
          const decoded = jwtDecode<JwtPayload>(access)
          set({
            accessToken: access,
            isAuthenticated: true,
            user: {
              id: decoded.user_id,
              email: decoded.email,
              full_name: decoded.full_name,
              is_platform_admin: decoded.is_platform_admin,
            },
            // JWT es slim: no contiene tenants. Los tenants ya están en el store
            // (desde el último login o desde la session cookie).
          })

          // Tras un refresh de token, refrescar activeTenant desde tenants ya almacenados
          const currentSchema = get().activeTenantSchema
          const storedTenants = get().tenants
          if (currentSchema && storedTenants.length > 0) {
            get().setActiveTenant(currentSchema)
          }
        } catch (e) {
          console.error('Error decodificando JWT:', e)
        }
      },

      // Recibe la lista de tenants desde el response body del login/SSO.
      // Separa la gestión de tenants de setTokens para poder actualizar ambos independientemente.
      setTenants: (tenants: TenantInfo[]) => {
        set({ tenants })
        const currentSchema = get().activeTenantSchema
        if (tenants.length === 1) {
          // Un solo tenant: seleccionar automáticamente (AUT-02 flujo alternativo)
          get().setActiveTenant(tenants[0].schema)
        } else if (currentSchema) {
          // Múltiples tenants: refrescar activeTenant con datos frescos (disabled_modules, permisos)
          get().setActiveTenant(currentSchema)
        }
      },

      setEmployeeRecord: (record) => {
        set({ employeeRecord: record })
      },

      setFirm: (firm) => {
        set({ firm })
      },

      setActiveTenant: (schema: string) => {
        const { tenants } = get()
        const tenant = tenants.find((t) => t.schema === schema)
        if (tenant) {
          set({
            activeTenantSchema: schema,
            activeTenant: tenant,
          })
        }
      },

      hydrateFromSession: (session) => {
        set({
          isAuthenticated: true,
          user: session.user,
          tenants: session.tenants,
          // activeTenant se setea después vía setActiveTenant (SelectTenantPage o ?t= param)
        })
      },

      logout: () => {
        set({
          accessToken: null,
          user: null,
          tenants: [],
          activeTenantSchema: null,
          activeTenant: null,
          employeeRecord: null,
          firm: null,
          isAuthenticated: false,
        })
      },
    }),
    {
      name: 'zen-auth',
      storage: createJSONStorage(() => localStorage),
      // accessToken NO se persiste en localStorage — se obtiene vía cookie de refresh en cada sesión.
      // Esto permite que al cambiar de subdominio (cross-domain), la zen_session cookie
      // hidrate user/tenants y el refresh cookie entregue un nuevo access token automáticamente.
      partialize: (state) => ({
        user: state.user,
        tenants: state.tenants,
        activeTenantSchema: state.activeTenantSchema,
        activeTenant: state.activeTenant,
        employeeRecord: state.employeeRecord,
        firm: state.firm,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
